import React from 'react';
import { ArrowLeft, Megaphone, Newspaper, Share2, BarChart3, CalendarDays, Heart, TrendingUp, Users, Brain, Scale, CheckCircle, BookOpen, ArrowRight, Network, GraduationCap, Radio } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ecePublicGoodCampaign } from '../data/programs';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ECEPublicGood = () => {
  const strategyIcons = {
    megaphone: Megaphone,
    newspaper: Newspaper,
    share: Share2,
    chart: BarChart3,
    calendar: CalendarDays,
    heart: Heart
  };

  const whyItMattersIcons = [TrendingUp, Users, Brain, Scale];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section id="main-content" role="main" tabIndex={-1} className="relative pt-20 pb-16 bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800">
        <div className="absolute inset-0">
          <img
            src={ecePublicGoodCampaign.heroImage}
            alt="ECE Advocacy"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/90 to-purple-900/80" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <Link to="/" className="inline-flex items-center gap-2 text-indigo-300 hover:text-indigo-200 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-2 bg-indigo-500/20 text-indigo-300 rounded-full text-sm font-semibold mb-4">
              DCELC Campaign
            </span>
            <div className="mb-6">
              <Link
                to="/programs/stronger-together"
                className="inline-flex items-center gap-1.5 text-teal-300 text-sm font-semibold underline hover:text-teal-200"
                data-testid="ece-part-of-stronger-together"
              >
                <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
                Part of the Stronger Together initiative
              </Link>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {ecePublicGoodCampaign.title}
            </h1>
            <p className="text-xl text-indigo-200 mb-8">
              {ecePublicGoodCampaign.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {ecePublicGoodCampaign.impact.stats.map((stat, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50">
                <p className="text-3xl font-bold text-indigo-600 mb-2">{stat.value}</p>
                <p className="text-sm text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Public Good Statement - Introduction */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
              The Public Good
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Time to Recognize Early Education and Care as Part of the Public Good
            </h2>
          </div>

          {/* Definition Box */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 mb-12 text-white">
            <p className="text-lg leading-relaxed">
              In economics, a <strong>public good</strong> refers to a commodity or service that is provided without profit to all members of a society, either by the government or a private individual or organization.
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none text-slate-700">
            <p className="mb-6 leading-relaxed">
              The pandemic of 2020 has shined a bright light on the inequities that have long existed in this country. Most pointedly, the disparities in access to health care and educational opportunities that lead to economic prosperity were laid bare. As we begin to defeat the corona virus and return to work, the critical role that the availability of quality child care will play in our country's return to work cannot be overstated. <strong>As such, early education and care must be recognized as a public good.</strong>
            </p>

            <p className="mb-6 leading-relaxed">
              Although historically the importance of early learning during child care has been overlooked, recently its importance has been heralded, particularly here in the District of Columbia. Similar to traditional public schools and public charter schools, Early Care and Education must be viewed as a part of the public good.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 my-8 rounded-r-xl">
              <p className="text-amber-800 font-medium mb-0">
                Since the Massachusetts Bay Colony General School Act of 1647, it has been widely agreed and accepted that public education is a part of the public good. It contributes to the overall cohesion and coherence of a community's moral fiber by enabling all of its children to reach their full potential.
              </p>
            </div>

            <p className="mb-6 leading-relaxed">
              Public education has long been recognized as the gateway to opportunity for children from a variety of racial/ethnic and socio-economic backgrounds. Early advocates of public education contended that only through public funding could we provide the consistent support needed to educate all children and bring a more standard approach to curriculum, length of school year, teacher qualifications and teacher compensation.
            </p>
          </div>

          {/* Key Facts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">$14 Return on Investment</h4>
              <p className="text-slate-600 text-sm">For every dollar invested in early education and care, economic models project a fourteen dollar return.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">60%+ of Workforce</h4>
              <p className="text-slate-600 text-sm">Much has changed since 1647, including women making up over 60% of the workforce, requiring quality child care.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-teal-100">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-teal-600" />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">Foundation for Success</h4>
              <p className="text-slate-600 text-sm">Early education and care is universally recognized as the foundation for the educational continuum that leads to success in school and life.</p>
            </div>
          </div>

          {/* DC Context */}
          <div className="bg-slate-800 rounded-2xl p-8 text-white mb-12">
            <h3 className="text-xl font-bold mb-4">The District of Columbia Context</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              Currently in the District, early education and care programs and services are being provided by Community Based Organizations (CBO), traditional public schools and public charter schools—all of which fall under the auspices of the Office of the State Superintendent of Education (OSSE).
            </p>
            <p className="text-slate-300 leading-relaxed">
              The Council of the District of Columbia's passage of the Pre-K Expansion and Enhancement Act of 2008 sought to address the disparities that existed in school-based and community-based pre-k programs serving 3 and 4 year olds. It recognized pre-k as a public good regardless of organizational auspices and provided a marker for equal pay for equal work and equal qualifications.
            </p>
          </div>

          {/* Call to Action Quote */}
          <div className="relative bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
            <div className="absolute -top-4 left-8 text-6xl text-indigo-300 font-serif">"</div>
            <blockquote className="text-xl text-slate-700 italic leading-relaxed pt-4">
              In view of the fact that early education and care serve as the initial building blocks of a child's educational and economic trajectory, <strong className="text-indigo-700">now is the time to recognize it as a critical part of the public good</strong> and use the same public dollars that support K-12 education to support Pre-K and Birth-to-3 education regardless of setting.
            </blockquote>
            <div className="mt-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">CT</div>
              <div>
                <p className="font-bold text-slate-800">Carrie Thornhill</p>
                <p className="text-slate-600 text-sm">President, DC Early Learning Collaborative</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-slate-700 leading-relaxed mb-8">
            {ecePublicGoodCampaign.intro}
          </p>
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
            <h3 className="text-xl font-bold text-slate-800 mb-4">{ecePublicGoodCampaign.approach.title}</h3>
            <p className="text-slate-700 leading-relaxed">
              {ecePublicGoodCampaign.approach.description}
            </p>
          </div>
        </div>
      </section>

      {/* Advocacy Photo Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
              Advocacy in Action
            </span>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Standing Up for Early Education</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              DCELC mobilizes educators, families, and community members to advocate for equitable funding and recognition of early childhood education.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={ecePublicGoodCampaign.heroImage}
                alt="ECE Advocacy Rally - Pay Equity"
                className="w-full h-80 object-cover"
              />
              <div className="bg-white p-6">
                <h4 className="font-bold text-slate-800 mb-2">Pay Equity for Educators</h4>
                <p className="text-slate-600 text-sm">Advocating for fair compensation that recognizes the essential work of early childhood educators.</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={ecePublicGoodCampaign.secondaryImage}
                alt="ECE Advocacy Rally - Stop Cuts"
                className="w-full h-80 object-cover"
              />
              <div className="bg-white p-6">
                <h4 className="font-bold text-slate-800 mb-2">Protecting ECE Funding</h4>
                <p className="text-slate-600 text-sm">Community members unite to prevent cuts to early childhood education programs that families depend on.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">{ecePublicGoodCampaign.whyItMatters.title}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {ecePublicGoodCampaign.whyItMatters.points.map((point, index) => {
              const Icon = whyItMattersIcons[index];
              const colors = [
                { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' },
                { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
                { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200' },
                { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' }
              ];
              const color = colors[index];
              
              return (
                <div key={index} className={`p-8 rounded-2xl bg-white border ${color.border} shadow-lg hover:shadow-xl transition-shadow duration-300`}>
                  <div className={`w-14 h-14 ${color.bg} rounded-xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-7 h-7 ${color.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{point.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{point.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Campaign Strategies */}
      <section className="py-16 bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-white/10 text-indigo-200 rounded-full text-sm font-semibold mb-4">
              Our Strategies
            </span>
            <h2 className="text-3xl font-bold mb-4">Campaign Pillars</h2>
            <p className="text-indigo-200 max-w-2xl mx-auto">
              DCELC leverages diverse media platforms to ensure broad and inclusive engagement across the District.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ecePublicGoodCampaign.strategies.map((strategy, index) => {
              const Icon = strategyIcons[strategy.icon];
              
              return (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-colors duration-300">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-indigo-200" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{strategy.title}</h3>
                  <p className="text-indigo-200 text-sm leading-relaxed">{strategy.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Research & Evidence */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-6">
                Evidence-Based
              </span>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">{ecePublicGoodCampaign.research.title}</h2>
              <p className="text-slate-600 mb-8">
                Our advocacy is grounded in decades of research demonstrating the transformative impact of early childhood education.
              </p>
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
                <BookOpen className="w-10 h-10 text-indigo-500" />
                <div>
                  <p className="font-semibold text-slate-800">Nobel Prize Research</p>
                  <p className="text-sm text-slate-500">James Heckman's groundbreaking ECE studies</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              {ecePublicGoodCampaign.research.points.map((point, index) => (
                <div key={index} className="flex items-start gap-4 p-5 bg-white rounded-xl shadow-sm border border-slate-100">
                  <CheckCircle className="w-6 h-6 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-sm leading-relaxed">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">{ecePublicGoodCampaign.callToAction.title}</h2>
          <p className="text-lg text-indigo-100 mb-10 leading-relaxed">
            {ecePublicGoodCampaign.callToAction.description}
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            {ecePublicGoodCampaign.callToAction.actions.map((action, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <ArrowRight className="w-5 h-5 text-indigo-200 flex-shrink-0" />
                <span className="text-white text-sm">{action}</span>
              </div>
            ))}
          </div>
          
          <Button
            onClick={() => window.location.href = '/#contact'}
            size="lg"
            className="bg-white text-indigo-600 hover:bg-indigo-50 px-8 py-6 text-lg font-semibold shadow-xl"
          >
            Get Involved Today
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ECEPublicGood;
